<script setup lang="ts">
/**
 * 약관·개인정보 동의 모달.
 *
 * 두 모드가 있다(notice prop).
 *  - notice=false (기본) : 하드 차단. 강제 기준(시행 도래분) 미동의자에게 뜨며 닫을 수 없다.
 *  - notice=true         : 안내. 시행 전 최신본이 게시됐을 때 "미리 동의" 를 권하는 용도로,
 *                          [나중에] 로 닫을 수 있고 그 버전은 이 기기에서 다시 뜨지 않는다.
 *                          이용은 막지 않는다(시행일이 오면 자동으로 차단 모드로 바뀐다).
 *
 * 차단 모드 설명 — 무조건 동의(persistent, 닫기 불가):
 *  - "동의합니다" 체크 후 [동의하고 계속] → POST /api/auth/accept-terms → 게이트 해제
 *  - [동의하지 않음] → 서비스 이용 불가 안내 후 로그아웃
 * 본문은 언어별로 /api/legal/{terms,privacy} 에서 로드(사용자 언어, 없으면 EN 폴백).
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { apiClient } from '@/shared/api/client'
import { renderMarkdown } from '@/shared/utils/miniMarkdown'
import { toLegalLang } from '@/shared/utils/legalLang'
import { markConsented, dismissUpcomingNotice } from '@/shared/composables/useTermsConsent'
import { useAuthStore } from '@/features/auth/stores/authStore'

const props = withDefaults(defineProps<{ modelValue: boolean; notice?: boolean }>(), { notice: false })
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>()

const { t, locale } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const declining = ref(false)

const tab = ref<'terms' | 'privacy'>('terms')
const agreed = ref(false)
const submitting = ref(false)
const errorMsg = ref('')
const termsHtml = ref('')
const privacyHtml = ref('')
const loading = ref(false)

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

function langParam(): string {
  // vue-i18n locale(en-GB/zh-tw 등) → DB LANG(EN/KO/MS/VN/ZH-CN/ZH-TW), 매칭 실패 시 EN
  return toLegalLang(locale.value)
}

async function loadDoc(type: 'terms' | 'privacy') {
  const res: any = await apiClient.get(`/legal/${type}?lang=${langParam()}`)
  const md = res?.data?.document?.CONTENT || res?.document?.CONTENT || ''
  return renderMarkdown(md, { stripLeadingLetterhead: true })
}

async function loadAll() {
  loading.value = true
  errorMsg.value = ''
  try {
    termsHtml.value = await loadDoc('terms')
    privacyHtml.value = await loadDoc('privacy')
  } catch (e: any) {
    errorMsg.value = t('common.legalConsent.loadError')
  } finally {
    loading.value = false
  }
}

async function onAgree() {
  if (!agreed.value || submitting.value) return
  submitting.value = true
  errorMsg.value = ''
  try {
    await apiClient.post('/auth/accept-terms', {})
    markConsented()
    open.value = false
    // 차단 중 접근 못하던 데이터 갱신을 위해 현재 라우트 재진입.
    router.replace({ path: router.currentRoute.value.fullPath, query: { ...router.currentRoute.value.query, _refresh: Date.now() } })
  } catch (e: any) {
    errorMsg.value = t('common.legalConsent.submitError')
  } finally {
    submitting.value = false
  }
}

// 안내 모드의 [나중에] — 닫기만 한다. 해당 버전은 이 기기에서 다시 안 뜬다.
function onLater() {
  dismissUpcomingNotice()
  open.value = false
}

async function onDecline() {
  // 거부 = 서비스 이용 불가 → 로그아웃. '/auth/logout'은 라우트가 아니라
  // authStore.logout() 액션이라 router.push('/auth/logout')로는 404가 난다.
  if (declining.value) return
  declining.value = true
  try {
    await authStore.logoutWithRedirect(router)
  } finally {
    declining.value = false
  }
}

watch(open, (v) => { if (v) loadAll() })
watch(() => locale.value, () => { if (open.value) loadAll() })
onMounted(() => { if (open.value) loadAll() })
</script>

<template>
  <!-- 안내 모드는 닫을 수 있어야 하므로 persistent 를 끈다. -->
  <v-dialog v-model="open" :persistent="!props.notice" max-width="900" :scrim="true">
    <v-card class="terms-consent-card">
      <v-card-title class="text-h6 font-weight-bold pt-5 px-6">
        {{ props.notice ? t('common.legalConsent.noticeTitle') : t('common.legalConsent.title') }}
      </v-card-title>

      <v-card-subtitle class="px-6 pb-1 consent-subtitle">
        {{ props.notice ? t('common.legalConsent.noticeBody') : t('common.legalConsent.subtitle') }}
      </v-card-subtitle>

      <v-tabs v-model="tab" class="px-4" density="comfortable">
        <v-tab value="terms">{{ t('common.legalConsent.tabTerms') }}</v-tab>
        <v-tab value="privacy">{{ t('common.legalConsent.tabPrivacy') }}</v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text class="terms-consent-body px-6" style="max-height: 52vh; overflow-y: auto;">
        <div v-if="loading" class="py-8 text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="errorMsg" class="py-6 text-error">{{ errorMsg }}</div>
        <div v-else>
          <div v-show="tab === 'terms'" class="md-content" v-html="termsHtml" />
          <div v-show="tab === 'privacy'" class="md-content" v-html="privacyHtml" />
        </div>
      </v-card-text>

      <v-divider />

      <div class="px-6 pt-3">
        <!-- 차단 모드: 거부 불가 명시 / 안내 모드: §12 해지·환불권 안내 -->
        <v-alert
          :type="props.notice ? 'warning' : 'info'"
          variant="tonal"
          density="compact"
          class="mb-3 mandatory-notice-alert"
        >
          {{ props.notice ? t('common.legalConsent.noticeRight') : t('common.legalConsent.mandatoryNotice') }}
        </v-alert>
        <v-checkbox
          v-model="agreed"
          density="compact"
          hide-details
          color="primary"
        >
          <template #label>
            <span class="text-body-2">
              <!-- 이미 모달 안 탭에서 본문을 보여주고 있으니 링크로 만들 필요 없음 — 그냥 굵은 텍스트. -->
              {{ t('signUp.agreePrefix') }}
              <strong>{{ t('common.legalConsent.linkTerms') }}</strong>
              {{ t('signUp.agreeAnd') }}
              <strong>{{ t('common.legalConsent.linkPrivacy') }}</strong>.
            </span>
          </template>
        </v-checkbox>
      </div>

      <v-card-actions class="px-6 pb-5 pt-2">
        <v-btn
          v-if="props.notice"
          variant="text"
          color="grey"
          :disabled="submitting"
          @click="onLater"
        >
          {{ t('common.legalConsent.noticeLater') }}
        </v-btn>
        <v-btn
          v-else
          variant="text"
          color="grey"
          :disabled="submitting || declining"
          :loading="declining"
          @click="onDecline"
        >
          {{ t('common.legalConsent.declineButton') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!agreed || submitting || declining"
          :loading="submitting"
          @click="onAgree"
        >
          {{ props.notice ? t('common.legalConsent.noticeReview') : t('common.legalConsent.agreeButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.md-content :deep(h1),
.md-content :deep(h2) { font-size: 1rem; font-weight: 700; line-height: 1.4; margin: 1.1rem 0 .5rem; }
.md-content :deep(h3) { font-size: .92rem; font-weight: 700; line-height: 1.4; margin: .85rem 0 .4rem; }
.md-content :deep(p) { margin: .5rem 0; line-height: 1.65; font-size: .875rem; }
.md-content :deep(ul),
.md-content :deep(ol) { margin: .45rem 0 .45rem 1.2rem; font-size: .875rem; }
.md-content :deep(li) { margin: .2rem 0; line-height: 1.6; }
.md-content :deep(table) { border-collapse: collapse; width: 100%; margin: .65rem 0; font-size: .8rem; }
.md-content :deep(th),
.md-content :deep(td) { border: 1px solid rgba(0,0,0,.12); padding: .35rem .5rem; text-align: left; vertical-align: top; }
.md-content :deep(blockquote) { border-left: 3px solid rgba(0,0,0,.15); margin: .55rem 0; padding: .25rem .75rem; color: rgba(0,0,0,.7); background: rgba(0,0,0,.02); font-size: .82rem; }
.md-content :deep(hr) { border: none; border-top: 1px solid rgba(0,0,0,.1); margin: .85rem 0; }
.md-content :deep(a) { color: rgb(var(--v-theme-primary)); }
.md-content :deep(code) { background: rgba(0,0,0,.05); padding: .05rem .3rem; border-radius: 3px; font-size: .8rem; }

.md-content :deep(.legal-letterhead) { margin: 0 0 .75rem; padding-bottom: .6rem; border-bottom: 1px solid rgba(0,0,0,.08); }
.md-content :deep(.legal-letterhead p) { margin: .1rem 0; font-size: .72rem; line-height: 1.5; color: rgba(0,0,0,.55); }
.md-content :deep(.legal-letterhead strong) { font-weight: 600; color: rgba(0,0,0,.65); }

.mandatory-notice-alert :deep(.v-alert-title),
.mandatory-notice-alert :deep(.v-alert__content) { font-size: .8rem; line-height: 1.5; }
.mandatory-notice-alert :deep(.v-icon) { font-size: 18px; }

/* Vuetify 기본 v-card-subtitle 은 한 줄 말줄임이라 안내 문구가 중간에 끊긴다 — 줄바꿈 허용. */
.consent-subtitle { white-space: normal; overflow: visible; text-overflow: clip; line-height: 1.55; opacity: 1; }

/* 체크박스 안 약관/개인정보처리방침 링크 — 사이드바 하단 링크·가입 폼과 동일한 톤 */
.legal-inline-link {
    color: rgba(var(--v-theme-on-surface), 0.75);
    font-weight: 500;
    text-decoration: none;
}
.legal-inline-link:hover {
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
}
</style>
